<script setup lang="ts">
import PreviewSlider from '~/components/instruments/PreviewSlider.vue';
import MarketDevelopment from '~/components/homepage/MarketDevelopment.vue';
import StepByStepTimeline from '~/components/homepage/StepByStepTimeline.vue';
import Media from '~/components/homepage/Media.vue';
import BlogSlider from '~/components/homepage/BlogSlider.vue';
import Element from '~/components/homepage/Element.vue';
import { AFFILIATE_KEY, CODES_KEY } from '~/constants';

const {
  public: { cdnUrl },
} = useRuntimeConfig();
const { t, locale } = useI18n();
const route = useRoute();
const { addDays, isAfter } = useDateFns();
const blogStore = useBlogStore();
const localePath = useLocalePath();

useCustomSeoMeta({
  title: t('homepage.meta.title'),
  description: t('homepage.meta.content'),
});

const canShowMedia = computed(() => locale.value === 'cs');

function storeOrRemoveKeys(storeKey: string, value: string | undefined) {
  if (value) {
    localStorage.setItem(
      storeKey,
      JSON.stringify({
        key: value,
        validTo: addDays(new Date(), 30),
      }),
    );
  }

  const storeValueRaw = localStorage.getItem(storeKey);
  if (storeValueRaw) {
    const storeValue = JSON.parse(storeValueRaw) as { key: string; validTo: string };
    if (isAfter(new Date(), new Date(storeValue.validTo))) {
      localStorage.removeItem(storeKey);
    }
  }
}

onMounted(() => {
  storeOrRemoveKeys(AFFILIATE_KEY, route.query.gtm_affiliate as string | undefined);

  const codes = Object.entries(route.query).reduce(
    (accumulated, [key, value]) => `${accumulated}${key}:${(value as string).split(',')[0]};`,
    '',
  );
  storeOrRemoveKeys(CODES_KEY, codes.length > 0 ? codes : undefined);
});

watch(
  canShowMedia,
  (newValue) => {
    if (newValue) {
      blogStore.getHomepagePosts({});
    }
  },
  { immediate: true },
);
</script>

<template>
  <section
    :id="$t('homepage.sections.intro')"
    class="background--light section--bottom-radius introduction"
  >
    <div class="container">
      <div class="row">
        <div class="col justify-center">
          <h1>{{ $t('homepage.intro.title') }}</h1>

          <p v-html="$t('homepage.intro.text')" />

          <Button as="NuxtLink" theme="light" :to="localePath('how-does-it-work')">
            {{ $t('homepage.intro.action') }}
          </Button>
        </div>

        <div class="col justify-center">
          <picture>
            <img
              alt=""
              aria-hidden="true"
              class="cover-image"
              width="540"
              height="480"
              :src="`${cdnUrl}/images/redesign/homepage/cover.png`"
              :srcset="`${cdnUrl}/images/redesign/homepage/cover@2x.png 2x, ${cdnUrl}/images/redesign/homepage/cover@3x.png 3x`"
            />
          </picture>
        </div>
      </div>
    </div>
  </section>

  <section :id="$t('homepage.sections.instruments-preview')">
    <PreviewSlider />
  </section>

  <section :id="$t('homepage.sections.why-invest-with-portu')" class="why-invest-with-portu">
    <div class="container">
      <div class="row">
        <div class="col">
          <h2 class="section__title">
            {{ $t('homepage.why-invest-with-portu.title') }}
          </h2>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="card card--dark">
            <svg
              width="80"
              height="81"
              viewBox="0 0 80 81"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M44.563 24.203c-2.125-.5-3.938-1.5-6.063-4.375-3.562 3.562-8.562 3.562-12.875 2.875m20.688 23.812-3.126-1.125c-.812-.313-1.374-1-1.437-1.875l-.688-4.813M11.189 67.89H61.5v-3.437m-32-25.75-.688 4.812a2.376 2.376 0 0 1-1.437 1.875L16 49.515a9.905 9.905 0 0 0-4.25 2.875 11.415 11.415 0 0 0-1.938 3.125c-.187.437-.312.875-.437 1.375a7.206 7.206 0 0 0-.188 1.625L9 67.952m16.875-45.374v7.25c0 2.812 1.313 5.625 3.313 7.375 1.062.937 2.937 1.937 4.812 2.687.438.188.875.25 1.313.25.437 0 .874-.125 1.312-.25 1.813-.688 3.688-1.75 4.813-2.688 2-1.687 3.312-4.562 3.312-7.374V23.39s.125-3.813 0-4.938c-.375-3-2.813-3-4.625-3-1.438-2.124-4-2.374-6.438-2.124-2.437.25-5.187.625-7.5-.188-2.125 4.938-.187 9.063-.187 9.688m42.438 6.5a8.625 8.625 0 1 1-17.25 0 8.625 8.625 0 0 1 17.25 0Z"
                stroke="#fff"
                stroke-miterlimit="10"
              />
              <path
                d="M28.063 46.828a7.82 7.82 0 0 0 7.187 4.75c3.188 0 6-1.938 7.188-4.75m7.812 10.437v5H69v-5h-2.188m-15.312-10v5h18.75v-5h-3.438m-13.75-10H49v5h18.75v-5h-1.563m-18.437 5v5H66.5v-3.5m-16.563 8.5H47.75v5H66.5v-3.438m-1.063-24.5c0 3.125-2.5 5.625-5.624 5.625a5.602 5.602 0 0 1-5.626-5.624c0-3.125 2.5-5.625 5.626-5.625"
                stroke="#E52400"
                stroke-miterlimit="10"
              />
            </svg>

            <h5>
              {{ $t('homepage.why-invest-with-portu.reasons.democratization-of-investing.title') }}
            </h5>

            <span>{{
              $t('homepage.why-invest-with-portu.reasons.democratization-of-investing.text')
            }}</span>
          </div>
        </div>

        <div class="col">
          <div class="card card--dark">
            <svg
              width="80"
              height="81"
              viewBox="0 0 80 81"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M23.99 69.457v-15.31H6v15.31m43.245 0V45.453h-17.99v24.006m43.248-.001V29.129h-17.99v40.328"
                stroke="#fff"
                stroke-miterlimit="10"
              />
              <path
                d="M68.546 11.14 40.192 39.494 27.445 26.747 6 48.19m62.546-37.05H56.514m12.032 0v12.033"
                stroke="#E52400"
                stroke-miterlimit="10"
              />
            </svg>

            <h5>
              {{ $t('homepage.why-invest-with-portu.reasons.portfolio-diversification.title') }}
            </h5>

            <span>{{
              $t('homepage.why-invest-with-portu.reasons.portfolio-diversification.text')
            }}</span>
          </div>
        </div>

        <div class="col">
          <div class="card card--dark">
            <svg
              width="80"
              height="81"
              viewBox="0 0 80 81"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M16.726 23.256 7 39.43l32.516 32.742L72.03 39.43l-9.726-16.173m-45.579 0h45.579m-45.579 0L28.15 39.43m34.156-16.173L50.939 39.43m-22.79 0 9.33 26.862M50.94 39.43l-9.333 26.69M9.998 39.43H69.26M39.517 23.256l-9.84 14.024m19.735 0-9.896-14.024"
                stroke="#fff"
                stroke-miterlimit="10"
              />
              <path
                d="m17.857 9.232 8.482 8.143m34.834-8.143-8.426 8.143m-13.231 0V7.14"
                stroke="#E52400"
                stroke-miterlimit="10"
              />
            </svg>

            <h5>
              {{ $t('homepage.why-invest-with-portu.reasons.interesting-returns.title') }}
            </h5>

            <span>{{ $t('homepage.why-invest-with-portu.reasons.interesting-returns.text') }}</span>
          </div>
        </div>

        <div class="col">
          <div class="card card--dark">
            <svg
              width="80"
              height="81"
              viewBox="0 0 80 81"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M41.6 18.609h19.688V62.67M26.913 73.61a8.75 8.75 0 0 1-8.75-8.75V54.546m8.75 19.063a8.75 8.75 0 0 0 8.75-8.75h38.124a8.75 8.75 0 0 1-8.75 8.75H26.912Zm15.625-40h13.125m-8.125 11.25h8.125m-30.938 11.25h30.938M38.343 20.44c0 1.125-1.656 2.012-1.93 3.05-.276 1.038.693 2.669.15 3.606-.544.938-2.426.894-3.2 1.669-.776.775-.72 2.656-1.67 3.2s-2.5-.438-3.606-.15c-1.106.287-1.925 1.931-3.043 1.931-1.12 0-2.013-1.656-3.05-1.931-1.038-.275-2.67.694-3.607.15-.937-.544-.893-2.419-1.668-3.2-.775-.781-2.62-.719-3.2-1.669-.582-.95.437-2.537.15-3.606-.288-1.069-1.932-1.925-1.932-3.05 0-1.125 1.657-2.006 1.932-3.044.275-1.037-.694-2.669-.15-3.606.543-.938 2.43-.894 3.2-1.669.768-.775.718-2.65 1.668-3.2.95-.55 2.538.438 3.607.15 1.068-.287 1.925-1.931 3.05-1.931 1.125 0 2.012 1.656 3.043 1.931 1.032.275 2.67-.693 3.607-.15.937.544.9 2.431 1.668 3.2.77.769 2.65.719 3.2 1.669.55.95-.437 2.538-.15 3.606.288 1.069 1.932 1.925 1.932 3.044Z"
                stroke="#fff"
                stroke-miterlimit="10"
              />
              <path
                d="M13.85 29.378 7 46.47l7.506-.85 4.844 5.8 6.669-16.65m10.212-5.393 6.856 17.093-7.512-.85-4.838 5.8-4.83-12.043m7.58-18.938a8.444 8.444 0 1 1-16.887 0 8.444 8.444 0 0 1 16.888 0Z"
                stroke="#E52400"
                stroke-miterlimit="10"
              />
            </svg>

            <h5>
              {{ $t('homepage.why-invest-with-portu.reasons.expertise-guaranteed.title') }}
            </h5>

            <span>{{
              $t('homepage.why-invest-with-portu.reasons.expertise-guaranteed.text')
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section :id="$t('homepage.sections.element')">
    <Element />
  </section>

  <section :id="$t('homepage.sections.market-development')">
    <div class="container">
      <div class="row">
        <div class="col">
          <h2 class="section__title">
            {{ $t('homepage.market-development.title') }}
          </h2>

          <p class="description">
            {{ $t('homepage.market-development.content') }}
          </p>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <MarketDevelopment />
        </div>
      </div>
    </div>
  </section>

  <section :id="$t('homepage.sections.step-by-step')">
    <div class="container">
      <div class="row">
        <div class="col">
          <h2 class="section__title section__title--left">
            {{ $t('homepage.step-by-step.title') }}
          </h2>
        </div>
      </div>

      <StepByStepTimeline />

      <div class="row">
        <div class="col col--step-by-step-actions">
          <Button as="NuxtLink" :to="localePath('registration')">{{
            $t('homepage.step-by-step.actions.want-to-begin')
          }}</Button>
        </div>
      </div>
    </div>
  </section>

  <section v-if="canShowMedia" :id="$t('homepage.sections.media')">
    <div class="container">
      <div class="row">
        <div class="col">
          <h2 class="section__title">
            {{ $t('homepage.media.title') }}
          </h2>
        </div>
      </div>

      <Media />
    </div>
  </section>

  <section v-if="canShowMedia" :id="$t('homepage.sections.blog')">
    <div class="container">
      <div class="row">
        <div class="col">
          <h2 class="section__title">
            {{ $t('homepage.blog.title') }}
          </h2>
        </div>
      </div>

      <BlogSlider />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.why-invest-with-portu {
  .row:nth-of-type(2) {
    @media (min-width: 768px) and (max-width: 1199.98px) {
      > * {
        flex: 1;
        width: 50%;
        max-width: calc(50% - (var(--gap) / 2));
        flex-basis: auto;
      }
    }
  }
  .card h5 {
    margin-top: 1.25rem;
  }
}

.col--step-by-step-actions {
  display: flex;
  justify-content: flex-end;

  .button {
    margin-top: 2.25rem;
  }
}
</style>
